import {
    CreateJobApplicationParams,
    createRpcClient,
    employmentTypeLabel,
    fileIcon,
    formatDate,
    formatNumber,
    type JobPostingPublicDTO,
    MaxFileSize,
    MimeType,
    parseDateString,
    patterns,
    toDateString,
    version,
} from "@pentacode/core";
import type { DateString } from "@pentacode/openapi";
import "@pentacode/ui/components";
import { css, html, LitElement } from "lit";
import { customElement, property, query, state } from "lit/decorators.js";
import "../../components/avatar/avatar";
import type { Avatar } from "../../components/avatar/avatar";
import "../../components/documents/file-input";
import type { FileInput } from "../../components/documents/file-input";
import "../../components/rich-text/rich-content";
import { Textarea } from "../../components/text-area/textarea";
import { PUBLIC_PTC_GOOGLE_STATIC_MAPS_KEY, PUBLIC_PTC_SERVER_URL } from "../../env";
import { restart } from "../../lib/restart";
import { singleton } from "../../lib/singleton";
import { readFileAsDataURL } from "../../lib/util";
import { config, shared } from "../../styles";
import { alert } from "../alert-dialog";
import "./images-dialog";
import type { ImagesDialog } from "./images-dialog";

const PHONE_PATTERN = new RegExp(patterns.DE.phone_number);
const CITY_PATTERN = new RegExp(patterns.DE.city_pattern, "u");

@customElement("ptc-jobs-app")
export class JobsApp extends LitElement {
    readonly routePattern = /^\/postings\/(?<uuid>[^/]+)/;

    @property({ type: Boolean, reflect: true, attribute: "singleton-container" })
    readonly singletonContainer = true;

    @state()
    private _posting: JobPostingPublicDTO | null = null;

    @state()
    private _loading = false;

    @state()
    private _editingAvailableFrom = false;

    @state()
    private _loadMapSnippet = false;

    @state()
    private _documents: File[] = [];

    @query("form")
    private _form: HTMLFormElement;

    @query("input[name='firstName']")
    private _firstNameInput: HTMLInputElement;

    @query("ptc-avatar")
    private _avatar: Avatar;

    @query("button[name='applyNow']")
    private _applyNowButton: HTMLButtonElement;

    @singleton("ptc-jobs-images-dialog")
    private _imagesDialog: ImagesDialog;

    private _observer: IntersectionObserver;

    private _api = createRpcClient(PUBLIC_PTC_SERVER_URL, () => {
        return {
            version: version, // TODO: replace with fullVersion for higher precision
            userAgent: navigator.userAgent,
            currentUrl: window.location.href,
            screenWidth: window.screen.width,
            screenHeight: window.screen.height,
        };
    });

    constructor() {
        super();
        // load in the background
        void this.load();
    }

    async load() {
        const uuid = window.location.pathname.match(this.routePattern)?.groups?.uuid;

        if (uuid) {
            this._loading = true;

            try {
                this._posting = await this._api.getJobPosting(uuid);
                document.title = `Mitarbeiter gesucht: ${this._posting.title} | Pentacode Jobs`;
            } catch (e) {
                void alert(e.message, { type: "warning" });
            }

            this._loading = false;
        }

        const loadingScreen = document.querySelector(".loading-screen");
        loadingScreen?.setAttribute("aria-busy", "false");
    }

    connectedCallback() {
        super.connectedCallback();
        this._observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        this._applyNowButton.style.display = "none";
                    } else {
                        this._applyNowButton.style.display = "";
                    }
                });
            },
            {
                root: null,
                threshold: 1,
            }
        );
    }

    updated() {
        if (this._firstNameInput) {
            this._observer.observe(this._firstNameInput);
        }
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this._observer.disconnect();
    }

    private _validateInput(name: string, isValid: boolean, message: string, report = false): boolean {
        const input = this._form.querySelector<HTMLInputElement | HTMLTextAreaElement>(`[name='${name}']`);
        if (!input) {
            return true;
        }

        input.setCustomValidity(isValid ? "" : message);

        if (report && !isValid) {
            this._form.reportValidity();
        }

        return isValid;
    }

    private _validateField(input: HTMLInputElement | HTMLTextAreaElement, report = false): boolean {
        const value = input.value.trim();
        const today = toDateString(new Date());

        switch (input.name) {
            case "address":
                return this._validateInput(
                    "address",
                    value.length >= 5,
                    "Bitte gib eine vollständige Adresse ein.",
                    report
                );
            case "city":
                return this._validateInput(
                    "city",
                    CITY_PATTERN.test(value),
                    "Bitte gib einen gültigen Ortsnamen ein.",
                    report
                );
            case "phone":
                return this._validateInput(
                    "phone",
                    !value || PHONE_PATTERN.test(value),
                    "Bitte gib eine gültige Telefonnummer ein.",
                    report
                );
            case "birthday":
                return this._validateInput(
                    "birthday",
                    Boolean(value) && Boolean(parseDateString(value)) && value >= "1900-01-01" && value <= today,
                    "Bitte gib ein gültiges Geburtsdatum ein.",
                    report
                );
            case "availableFrom":
                return this._validateInput(
                    "availableFrom",
                    !value || (Boolean(parseDateString(value)) && value >= today),
                    "Bitte gib ein gültiges Verfügbarkeitsdatum ein.",
                    report
                );
            default:
                input.setCustomValidity("");
                return true;
        }
    }

    private _validateEditedField(e: Event) {
        const input = e.target;
        if (!(input instanceof HTMLInputElement || input instanceof HTMLTextAreaElement)) {
            return;
        }

        this._validateField(input);
    }

    private async _submit(e: Event) {
        e.preventDefault();

        const data = new FormData(this._form);
        const email = data.get("email") as string;
        const firstName = data.get("firstName") as string;
        const lastName = data.get("lastName") as string;
        const birthday = (data.get("birthday") as DateString) || null;
        const availableFrom = (data.get("availableFrom") as DateString) || null;
        const address = data.get("address") as string;
        const phone = data.get("phone") as string;
        const postalCode = data.get("postalCode") as string;
        const city = data.get("city") as string;
        const message = data.get("message") as string;
        const headShot = this._avatar.newImage;
        const documents = await Promise.all(
            this._documents.map(async (file) => ({
                name: file.name,
                dataUrl: await readFileAsDataURL(file),
            }))
        );

        const trimmedAddress = address.trim();
        const trimmedPhone = phone.trim();
        const trimmedPostalCode = postalCode.trim();
        const trimmedCity = city.trim();

        if (
            !["address", "city", "phone"].every((name) => {
                const input = this._form.querySelector<HTMLInputElement | HTMLTextAreaElement>(`[name='${name}']`);
                return input ? this._validateField(input, true) : true;
            })
        ) {
            return;
        }

        const today = toDateString(new Date());

        // If birthday is input, it must be a valid date and between 1900-01-01 and today
        if (!birthday || !parseDateString(birthday) || birthday < "1900-01-01" || birthday > today) {
            const input = this._form.querySelector("input[name='birthday']") as HTMLInputElement;
            input.setCustomValidity("Bitte gib ein gültiges Geburtsdatum ein.");
            this._form.reportValidity();
            return;
        }

        // If availableFrom is input, it must be a valid date, today or later
        if (availableFrom && (!parseDateString(availableFrom) || availableFrom < today)) {
            const input = this._form.querySelector("input[name='availableFrom']") as HTMLInputElement;
            input.setCustomValidity("Bitte gib ein gültiges Verfügbarkeitsdatum ein.");
            this._form.reportValidity();
            return;
        }

        this._loading = true;

        try {
            await this._api.createJobApplication(
                new CreateJobApplicationParams({
                    postingUuid: this._posting!.uuid,
                    email,
                    firstName,
                    lastName,
                    birthday,
                    availableFrom,
                    address: trimmedAddress,
                    postalCode: trimmedPostalCode,
                    city: trimmedCity,
                    phone: trimmedPhone,
                    headShot,
                    message,
                    documents,
                    // documents: [
                    //     {
                    //         name: "Lebenslauf.pdf",
                    //         dataUrl: "data:application/pdf;base64,base64encodeddata",
                    //     },
                    // ],
                })
            );
            await alert("Deine Bewerbung wurde erfolgreich übermittelt. Viel Erfolg! 🤞", {
                icon: "icon.action.publish",
                type: "success",
                title: "Bewerbung Abgeschickt",
            });
            restart();
        } catch (e) {
            void alert(e.message, { type: "warning" });
        }

        this._loading = false;
    }

    private _documentSelected(e: Event) {
        const input = e.currentTarget as FileInput;
        const file = input.file;
        if (!file) {
            return;
        }
        if (file.size > MaxFileSize) {
            void alert("Dateien dürfen nicht größer als 10MB sein!", { title: "Datei zu groß", type: "warning" });
            input.file = null;
            return;
        }
        this._documents = [...this._documents, file];
        input.file = null;
    }

    private _removeDocument(index: number) {
        this._documents = this._documents.filter((_, i) => i !== index);
    }

    static styles = [
        config,
        shared,
        Textarea.styles,
        css`
            h1 {
                font-size: 2em;
            }

            .sidebar {
                width: 20em;
                position: sticky;
                top: 0;
                margin-top: 7em;
            }

            .venue-location {
                width: 100%;
                border-radius: 0.5em;
                box-shadow: rgb(0 0 0 / 30%) 0 1px 3px;
                margin-bottom: 0.5em;
            }

            .images {
                display: grid;
                grid-template-columns: 1fr 1fr;
                grid-gap: 0.5em;
                margin-bottom: 1em;
            }

            .image-container {
                position: relative;
                padding-bottom: 75%;
                cursor: pointer;
            }

            .title-image {
                padding-bottom: 50%;
                margin-bottom: 1.5em;
            }

            .image-container img {
                position: absolute;
                width: 100%;
                height: 100%;
                object-fit: cover;
                object-position: center center;
                border-radius: 0.5em;
                box-shadow: rgb(0 0 0 / 30%) 0 1px 3px;
            }

            .inline-details {
                margin: 2em 0;
            }

            @media (max-width: 800px) {
                .side-details {
                    display: none;
                }

                h1 {
                    margin-top: 0;
                }
            }

            @media (min-width: 801px) {
                .inline-details {
                    display: none;
                }
            }
        `,
    ];

    private _renderDetails({
        employmentType,
        payAmount,
        payType,
        venue,
        start,
        end,
        hoursPerWeek,
        imageUrls,
    }: JobPostingPublicDTO) {
        return html`
            <div class="margined blue semibold colored-text">Beschäftigungsverhältnis</div>

            <div class="margined">${employmentTypeLabel(employmentType)}</div>

            <div class="horizontal evenly stretching layout">
                <div>
                    <div class="margined blue semibold colored-text">Beginn</div>

                    <div class="margined">${start ? formatDate(start) : "Ab Sofort"}</div>
                </div>

                <div>
                    <div class="margined blue semibold colored-text">Ende</div>

                    <div class="margined">${end ? formatDate(end) : "Unbegrenzt"}</div>
                </div>
            </div>

            <div class="horizontal evenly stretching layout">
                <div>
                    <div class="margined blue semibold colored-text">Arbeitszeit</div>

                    <div class="margined">
                        ${hoursPerWeek ? `${formatNumber(hoursPerWeek, 0)} St. / Woche` : "Keine Angabe"}
                    </div>
                </div>

                <div>
                    <div class="margined blue semibold colored-text">Bezahlung</div>

                    <div class="margined">
                        ${payAmount
                            ? `${formatNumber(payAmount)} € / ${payType === "monthly" ? "Monat" : "Stunde"}`
                            : "Keine Angabe"}
                    </div>
                </div>
            </div>

            <div class="margined blue semibold colored-text">Arbeitsort</div>

            <div class="margined">
                <strong>${venue.name}</strong> <br />
                ${venue.address} <br />
                ${venue.postalCode} ${venue.city}
            </div>

            ${this._renderVenueMap(venue)}

            <div>
                <div class="margined blue semibold colored-text">Eindrücke</div>
                <div class="images">
                    ${imageUrls.map(
                        (img, index) =>
                            html` <div
                                class="image-container"
                                @click=${() => this._imagesDialog.show({ images: imageUrls, index })}
                            >
                                <img src="${img}" />
                            </div>`
                    )}
                </div>
            </div>
        `;
    }

    private _renderVenueMap(venue: JobPostingPublicDTO["venue"]) {
        if (!PUBLIC_PTC_GOOGLE_STATIC_MAPS_KEY) {
            return "";
        }

        if (!this._loadMapSnippet) {
            return html`
                <button class="small subtle" @click=${() => (this._loadMapSnippet = true)}>
                    <ptc-ui-icon name="icon.action.show-on-map"></ptc-ui-icon>
                    Standort anzeigen (Google Maps)
                </button>
            `;
        }

        const query = encodeURIComponent(
            `${venue.name},${venue.address},${venue.postalCode} ${venue.city},${venue.state}`
        );
        return html`
            <a href="https://www.google.com/maps/search/?api=1&amp;query=${query}">
                <img
                    class="venue-location"
                    src="https://maps.googleapis.com/maps/api/staticmap?markers=${query}&amp;size=560x280&amp;scale=2&amp;key=${PUBLIC_PTC_GOOGLE_STATIC_MAPS_KEY}"
                />
            </a>
        `;
    }

    private _renderPosting(posting: JobPostingPublicDTO) {
        const { title, description, imageUrls } = posting;
        return html`
            <div class="padded vertical layout" style="width: 100%; max-width: 60em;">
                <div class="padded spacing horizontal layout">
                    <div class="padded stretch">
                        <h1>
                            <div class="subtle small semibold">Mitarbeiter gesucht:</div>
                            <div class="blue colored-text">${title}</div>
                        </h1>

                        ${imageUrls.length
                            ? html`
                                  <div
                                      class="image-container title-image"
                                      @click=${() => this._imagesDialog.show({ images: imageUrls, index: 0 })}
                                  >
                                      <img src=${imageUrls[0]} />
                                  </div>
                              `
                            : ""}

                        <ptc-rich-content class="margined" .content=${description}></ptc-rich-content>

                        <div class="inline-details">${this._renderDetails(posting)}</div>

                        <div style="max-width: 40em">
                            <h2>Jetzt Bewerben:</h2>

                            <form @submit=${this._submit}>
                                <div class="horizontal center-aligning spacing layout">
                                    <ptc-avatar class="enormous" .employee=${{}} editable></ptc-avatar>
                                    <div class="stretch">
                                        <div class="field">
                                            <label>Vorname</label>
                                            <input name="firstName" required />
                                        </div>
                                        <div class="field">
                                            <label>Nachname</label>
                                            <input name="lastName" required />
                                        </div>
                                    </div>
                                </div>
                                <div class="horizontal spacing evenly stretching layout">
                                    <div class="field">
                                        <label> Geburtstag </label>
                                        <input name="birthday" type="date" @change=${this._validateEditedField} />
                                    </div>
                                    <div class="field">
                                        <label> Verfügbar Ab </label>
                                        ${this._editingAvailableFrom
                                            ? html`
                                                  <input
                                                      name="availableFrom"
                                                      type="date"
                                                      @change=${this._validateEditedField}
                                                  />
                                              `
                                            : html`
                                                  <div class="horizontal half-padded layout">
                                                      <div class="subtle bold padded">Sofort</div>
                                                      <button
                                                          class="slim transparent"
                                                          @click=${() => (this._editingAvailableFrom = true)}
                                                      >
                                                          <ptc-ui-icon name="icon.action.edit"></ptc-ui-icon>
                                                      </button>
                                                  </div>
                                              `}
                                    </div>
                                </div>
                                <div class="horizontal spacing evenly stretching layout">
                                    <div class="field">
                                        <label> Email </label>
                                        <input name="email" type="email" required />
                                    </div>
                                    <div class="field">
                                        <label> Telefon </label>
                                        <input name="phone" type="tel" @input=${this._validateEditedField} />
                                    </div>
                                </div>
                                <div class="field">
                                    <label> Adresse </label>
                                    <input name="address" required @input=${this._validateEditedField} />
                                </div>
                                <div class="horizontal spacing evenly stretching layout">
                                    <div class="field">
                                        <label> Postleitzahl </label>
                                        <input name="postalCode" required @input=${this._validateEditedField} />
                                    </div>
                                    <div class="field">
                                        <label> Stadt </label>
                                        <input name="city" required @input=${this._validateEditedField} />
                                    </div>
                                </div>
                                <div class="field">
                                    <label>Nachricht</label>
                                    <ptc-textarea
                                        name="message"
                                        placeholder="Erzähle etwas über deine Person oder beantworte in der Ausschreibung gestellte Fragen."
                                        maxLength="500"
                                    ></ptc-textarea>
                                </div>
                                <div class="field">
                                    <label>Dokumente</label>
                                    <ptc-file-input
                                        @change=${this._documentSelected}
                                        .acceptTypes=${[
                                            MimeType.PDF,
                                            MimeType.MSWord,
                                            MimeType.MSWordLegacy,
                                            MimeType.MSExcel,
                                            MimeType.MSExcelLegacy,
                                            MimeType.PNG,
                                            MimeType.JPG,
                                            MimeType.WEBP,
                                        ]}
                                    ></ptc-file-input>
                                    ${this._documents.length
                                        ? html`
                                              <div class="top-margined vertical layout">
                                                  ${this._documents.map(
                                                      (file, index) => html`
                                                          <div class="box horizontal center-aligning layout">
                                                              <ptc-ui-icon
                                                                  name="${fileIcon(file.type as MimeType)}"
                                                                  class="larger"
                                                              ></ptc-ui-icon>
                                                              <div class="stretch ellipsis">${file.name}</div>
                                                              <button
                                                                  class="slim transparent"
                                                                  type="button"
                                                                  @click=${() => this._removeDocument(index)}
                                                              >
                                                                  <ptc-ui-icon name="icon.action.remove"></ptc-ui-icon>
                                                              </button>
                                                          </div>
                                                      `
                                                  )}
                                              </div>
                                          `
                                        : html`<div class="subtle small top-margined">10MB pro Datei.</div>`}
                                </div>
                                <div class="horizontal top-margined spacing evenly stretching layout">
                                    <button class="primary">
                                        <ptc-ui-icon name="icon.action.publish"></ptc-ui-icon> Absenden
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                    <div class="side-details">
                        <div class="padded sidebar">
                            ${this._renderDetails(posting)}

                            <div class="padded vertical layout">
                                <button name="applyNow" class="primary" @click=${() => this._firstNameInput?.focus()}>
                                    Jetzt Bewerben
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        `;
    }

    render() {
        return html`
            <div class="centering layout">${this._posting ? this._renderPosting(this._posting) : ""}</div>

            <div class="fullbleed center-aligning center-justifying vertical layout scrim" ?hidden=${!this._loading}>
                <ptc-ui-spinner ?active=${this._loading}></ptc-ui-spinner>
            </div>
        `;
    }
}
